import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
} from 'react-native';
import { StepWizardModal, stepWizardStyles } from '@/components/common/StepWizardModal';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { PostItemFormData, INITIAL_POST_ITEM_FORM } from '@/types/residents/marketplaceListing';
import { showConfirm, showAlert } from '@/utils/common/alert';
import {
  StepItemDetails,
  StepMedia,
  StepPriceCondition,
  StepPickupPreferences,
  StepSuccess,
} from './post-item';
import { 
  useCreateListingMutation, 
  useUpdateListingMutation, 
  mapConditionToBackend, 
  mapPickupOptionsToBackend 
} from '@/store/api';

type PostItemStep = 'details' | 'media' | 'price' | 'pickup' | 'success';

const STEPS: PostItemStep[] = ['details', 'media', 'price', 'pickup', 'success'];
const TOTAL_STEPS = STEPS.length;

interface PostItemModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmitSuccess?: () => void;
  initialData?: PostItemFormData;
  isEditMode?: boolean;
  listingId?: string;
  propertyId?: number;
}

export const PostItemModal: React.FC<PostItemModalProps> = ({
  visible,
  onClose,
  onSubmitSuccess,
  initialData,
  isEditMode = false,
  listingId,
  propertyId,
}) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [formData, setFormData] = useState<PostItemFormData>(initialData || INITIAL_POST_ITEM_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [createListing] = useCreateListingMutation();
  const [updateListing] = useUpdateListingMutation();
  
  React.useEffect(() => {
    if (visible) {
      setFormData(initialData || INITIAL_POST_ITEM_FORM);
      setCurrentStepIndex(0);
    }
  }, [visible, initialData]);

  const currentStep = STEPS[currentStepIndex];
  const isLastFormStep = currentStep === 'pickup';
  const isSuccessStep = currentStep === 'success';

  const handleFormChange = useCallback((updates: Partial<PostItemFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  }, []);

  const resetAndClose = useCallback(() => {
    setCurrentStepIndex(0);
    setFormData(INITIAL_POST_ITEM_FORM);
    onClose();
  }, [onClose]);

  const handleClose = useCallback(async () => {
    if (isSuccessStep) {
      resetAndClose();
      return;
    }

    const hasData = 
      formData.title.trim() !== '' || 
      formData.description.trim() !== '' || 
      formData.images.length > 0 ||
      formData.pickupOptions.length > 0;

    if (hasData) {
      const confirmed = await showConfirm(
        'Discard Changes?',
        isEditMode 
          ? 'Are you sure you want to discard your changes?'
          : 'Are you sure you want to discard your listing?',
        'Discard',
        'Cancel'
      );
      if (confirmed) {
        resetAndClose();
      }
    } else {
      resetAndClose();
    }
  }, [formData, isSuccessStep, isEditMode, resetAndClose]);

  const validateCurrentStep = useCallback((): boolean => {
    switch (currentStep) {
      case 'details':
        if (!formData.title.trim()) {
          showAlert('Title Required', 'Please enter a title for your item.');
          return false;
        }
        return true;
      case 'media':
        return true;
      case 'price':
        if (formData.priceType === 'set_price') {
          const price = parseFloat(formData.price);
          if (isNaN(price) || price < 0) {
            showAlert('Invalid Price', 'Please enter a valid price.');
            return false;
          }
        }
        return true;
      case 'pickup':
        if (formData.pickupOptions.length === 0) {
          showAlert('Pickup Required', 'Please select at least one pickup option.');
          return false;
        }
        return true;
      default:
        return true;
    }
  }, [currentStep, formData]);

  const handleNext = useCallback(() => {
    if (!validateCurrentStep()) return;

    if (isLastFormStep) {
      handleSubmit();
    } else {
      setCurrentStepIndex(prev => Math.min(prev + 1, TOTAL_STEPS - 1));
    }
  }, [validateCurrentStep, isLastFormStep]);

  const handleBack = useCallback(() => {
    setCurrentStepIndex(prev => Math.max(prev - 1, 0));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    
    try {
      const priceValue = formData.priceType === 'free' 
        ? 0 
        : parseFloat(formData.price) || 0;

      const existingImages = formData.images.filter(img => img.serverId !== undefined);
      const newImages = formData.images.filter(img => img.serverId === undefined);

      const imagesToUpload = newImages.map((img, index) => ({
        uri: img.uri,
        type: img.type || 'image/jpeg',
        name: img.name || `image-${index}.jpg`,
        file: img.file,
      }));

      const existingImageIds = existingImages
        .map(img => img.serverId)
        .filter((id): id is number => id !== undefined);

      if (isEditMode && listingId) {
        await updateListing({
          id: listingId,
          title: formData.title,
          description: formData.description,
          price: priceValue,
          condition: mapConditionToBackend(formData.condition),
          pickupOption: mapPickupOptionsToBackend(formData.pickupOptions),
          existingImageIds: existingImageIds.length > 0 ? existingImageIds : undefined,
          images: imagesToUpload.length > 0 ? imagesToUpload : undefined,
        }).unwrap();
        
        if (onSubmitSuccess) {
          onSubmitSuccess();
        }
      } else {
        if (!propertyId) {
          showAlert('Error', 'Unable to create listing. Please try again.');
          setIsSubmitting(false);
          return;
        }

        const allImages = formData.images.map((img, index) => ({
          uri: img.uri,
          type: img.type || 'image/jpeg',
          name: img.name || `image-${index}.jpg`,
          file: img.file,
        }));

        await createListing({
          title: formData.title,
          description: formData.description,
          price: priceValue,
          condition: mapConditionToBackend(formData.condition),
          pickupOption: mapPickupOptionsToBackend(formData.pickupOptions),
          propertyId: propertyId,
          images: allImages.length > 0 ? allImages : undefined,
        }).unwrap();
        
        setCurrentStepIndex(TOTAL_STEPS - 1);
        
        if (onSubmitSuccess) {
          onSubmitSuccess();
        }
      }
    } catch (error: any) {
      console.error('Failed to submit listing:', error);
      showAlert(
        'Error',
        error?.data?.message || 'Failed to submit listing. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, onSubmitSuccess, isEditMode, listingId, propertyId, isSubmitting, createListing, updateListing]);

  const handleBackToMarketplace = useCallback(() => {
    resetAndClose();
  }, [resetAndClose]);

  const isNextEnabled = useCallback((): boolean => {
    switch (currentStep) {
      case 'details':
        return formData.title.trim().length > 0;
      case 'media':
        return true;
      case 'price':
        if (formData.priceType === 'free') return true;
        const price = parseFloat(formData.price);
        return !isNaN(price) && price >= 0;
      case 'pickup':
        return formData.pickupOptions.length > 0;
      default:
        return true;
    }
  }, [currentStep, formData]);

  const renderStepContent = () => {
    switch (currentStep) {
      case 'details':
        return (
          <StepItemDetails
            formData={formData}
            onFormChange={handleFormChange}
          />
        );
      case 'media':
        return (
          <StepMedia
            mediaItems={formData.images}
            onMediaItemsChange={(items) => handleFormChange({ images: items })}
          />
        );
      case 'price':
        return (
          <StepPriceCondition
            formData={formData}
            onFormChange={handleFormChange}
          />
        );
      case 'pickup':
        return (
          <StepPickupPreferences
            formData={formData}
            onFormChange={handleFormChange}
          />
        );
      case 'success':
        return <StepSuccess />;
      default:
        return null;
    }
  };

  const renderFooter = isSuccessStep ? () => (
    <View style={styles.successFooter}>
      <TouchableOpacity
        style={stepWizardStyles.primaryButton}
        onPress={handleBackToMarketplace}
        activeOpacity={0.8}
      >
        <Text style={stepWizardStyles.primaryButtonText}>Back to marketplace</Text>
      </TouchableOpacity>
    </View>
  ) : undefined;

  return (
    <StepWizardModal
      visible={visible}
      onClose={handleClose}
      currentStepIndex={currentStepIndex}
      totalSteps={TOTAL_STEPS}
      onNext={handleNext}
      onBack={handleBack}
      isNextEnabled={isNextEnabled()}
      isSubmitting={isSubmitting}
      isLastStep={isLastFormStep}
      submitLabel={isEditMode ? 'Update' : 'Submit'}
      renderFooter={renderFooter}
    >
      {renderStepContent()}
    </StepWizardModal>
  );
};

const styles = StyleSheet.create({
  successFooter: {
    flexDirection: 'row',
    gap: 20,
    paddingHorizontal: 24,
    paddingTop: 24,
  },
});
